import { useCallback, useMemo } from 'react';

import { ITranslations } from '@model';
import {
  useHttpClientAsync,
  GRAPHQL_HEADER,
  httpMethods,
  IGeneralRequestResponse,
  IGraphQlQuery,
  IHttpHookResponse,
} from '@services/httpClientService';
import { ETranslationKeys } from '@services/localizationService';

import { ApiSectionNamespace } from '../apiConstants';

import { TRANSLATION_QUERY } from './graphql';
import { transformTranslations } from './mapper';
import { ITranslationsGatewayResponse } from './model';

const mappingFunction = (result: ITranslationsGatewayResponse): IGeneralRequestResponse<ITranslations> => ({
  ...result.translationByKey,
  data: transformTranslations(result.translationByKey.data),
});

export const useGetTranslationsOnDemand = (): IHttpHookResponse<
  IGeneralRequestResponse<ITranslations>,
  { keys: ETranslationKeys[] }
> => {
  const asyncRequest = useHttpClientAsync<
    IGeneralRequestResponse<ITranslations>,
    IGraphQlQuery,
    ITranslationsGatewayResponse
  >({
    method: httpMethods.POST,
    path: ApiSectionNamespace.ETranslationApiKeys.TRANSLATION_URL_2,
    mappingFunction,
    customHeaders: GRAPHQL_HEADER,
  });

  const { request } = asyncRequest;

  const requestOnDemand = useCallback(
    (data?: { keys: ETranslationKeys[] }) => {
      const queryUrlParams = data?.keys.map((key) => ({
        name: 'keys',
        value: key,
      }));

      return request({
        data: { query: TRANSLATION_QUERY },
        queryUrlParams,
      });
    },
    [request],
  );

  return useMemo(
    () => ({
      ...asyncRequest,
      request: requestOnDemand,
    }),
    [asyncRequest, requestOnDemand],
  );
};
